import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ArrowRight,
  UtensilsCrossed,
  MapPin,
  Clock,
  Heart,
  Milk,
  Apple,
  Flame,
  CheckCircle2,
  Instagram,
  Navigation,
} from "lucide-react";
import { businessInfo } from "../data/business";
import { staticMenuItems, menuCategories } from "../data/menu";
import { socialPosts } from "../data/social";

export default function Home() {
  const bestSellers = staticMenuItems.filter((item) => item.isPopular).slice(0, 6);
  const featuredSocial = socialPosts.slice(0, 4);

  return (
    <div className="space-y-16 sm:space-y-24 pb-16">
      {/* 🍨 HERO SECTION */}
      <section className="relative overflow-hidden pt-6 sm:pt-12 lg:pt-16">
        {/* Subtle decorative background circles */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-sky-100/50 via-amber-100/30 to-rose-100/20 rounded-full blur-3xl -z-10 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* LEFT CONTENT (7 cols) */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-50 border border-sky-200/60 text-sky-800 text-xs sm:text-sm font-bold shadow-xs">
                <Sparkles className="w-4 h-4 text-sky-600 animate-pulse" />
                <span>Udaipur ka Natural Ice Cream 🍦</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#2C3E50] tracking-tight leading-[1.15]">
                Naturally Delicious. <br />
                <span className="bg-gradient-to-r from-sky-600 via-sky-500 to-teal-500 bg-clip-text text-transparent">
                  Made Fresh in Udaipur.
                </span>
              </h1>

              <p className="text-base sm:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto lg:mx-0 font-normal">
                Crafted with 100% real seasonal fruits, pure rich milk, and time-honored artisanal methods. Pure natural goodness with zero artificial colors, synthetic flavors, or chemical preservatives.
              </p>

              {/* ACTION BUTTONS */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-2">
                <Link
                  to="/menu"
                  className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm sm:text-base px-6 sm:px-7 py-3 rounded-full shadow-lg shadow-sky-500/25 hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
                >
                  <span>Explore Menu</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/select-table"
                  className="inline-flex items-center gap-2 bg-white hover:bg-stone-50 text-[#2C3E50] border-2 border-stone-200 hover:border-sky-300 font-bold text-sm sm:text-base px-5 sm:px-6 py-3 rounded-full shadow-sm hover:scale-105 active:scale-95 transition-all"
                >
                  <UtensilsCrossed className="w-4 h-4 text-sky-600" />
                  <span>Order at Your Table</span>
                </Link>

                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 text-stone-600 hover:text-sky-600 font-semibold text-sm px-4 py-2 hover:bg-stone-100/60 rounded-full transition"
                >
                  <MapPin className="w-4 h-4 text-stone-400" />
                  <span>Visit Our Café</span>
                </Link>
              </div>

              {/* STATS STRIP */}
              <div className="pt-6 border-t border-stone-200/80 grid grid-cols-2 sm:grid-cols-4 gap-4 text-left">
                {businessInfo.stats.map((stat, i) => (
                  <div key={i} className="space-y-0.5">
                    <p className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50]">
                      {stat.value}
                    </p>
                    <p className="text-xs text-stone-500 font-medium">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT HERO IMAGE (5 cols) */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Main hero card */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-white">
                  <img
                    src="https://images.unsplash.com/photo-1560008581-09826d1de69e?w=800&auto=format&fit=crop&q=85"
                    alt="HimCream Natural Ice Cream"
                    className="w-full h-80 sm:h-96 object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-transparent to-transparent flex flex-col justify-end p-6 text-white">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-bold w-fit mb-1">
                      <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                      <span>Signature Saffron & Pista</span>
                    </div>
                    <h2 className="text-xl font-bold">Kesar Pista Natural Ice Cream</h2>
                    <p className="text-xs text-stone-200 mt-1">
                      Pure Kashmiri saffron & roasted California pistachios
                    </p>
                  </div>
                </div>

                {/* Floating badge top right */}
                <div className="absolute -top-4 -right-4 bg-white/95 backdrop-blur-md p-3.5 rounded-2xl shadow-xl border border-stone-100 hidden sm:flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-extrabold text-lg">
                    🍨
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-800">Fresh Daily</p>
                    <p className="text-[11px] text-stone-500">Zero Preservatives</p>
                  </div>
                </div>

                {/* Floating badge bottom left */}
                <div className="absolute -bottom-5 -left-4 bg-white/95 backdrop-blur-md p-3.5 rounded-2xl shadow-xl border border-stone-100 hidden sm:flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-extrabold text-lg">
                    🥭
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-800">Real Fruit Pulp</p>
                    <p className="text-[11px] text-stone-500">100% Pure Sourced</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 🌿 BRAND PILLARS / WHY HIMCREAM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-xs sm:text-sm font-bold uppercase tracking-widest text-sky-600 mb-2">
            The HimCream Promise
          </h2>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50] tracking-tight">
            Why Our Natural Ice Cream Tastes So Good
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {businessInfo.features.map((feature, idx) => {
            const icons = [
              <Apple className="w-6 h-6 text-rose-500" />,
              <Milk className="w-6 h-6 text-sky-500" />,
              <Sparkles className="w-6 h-6 text-amber-500" />,
              <Heart className="w-6 h-6 text-emerald-500" />,
            ];

            return (
              <div
                key={idx}
                className="bg-white rounded-3xl p-6 border border-stone-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-stone-50 flex items-center justify-center mb-4 border border-stone-100">
                    {icons[idx] || <Sparkles className="w-6 h-6 text-sky-500" />}
                  </div>
                  <h4 className="font-bold text-lg text-[#2C3E50] mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center gap-1.5 text-xs font-semibold text-sky-600">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Guaranteed Fresh</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 🔥 BEST SELLERS SHOWCASE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold uppercase tracking-wider text-amber-600 mb-1">
              <Flame className="w-4 h-4 text-amber-500" />
              <span>Customer Favourites</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50]">
              Signature Flavours
            </h3>
          </div>

          <Link
            to="/menu"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-600 hover:text-sky-700 group"
          >
            <span>View Full Menu</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {bestSellers.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-3xl overflow-hidden border border-stone-100 shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col justify-between"
            >
              <div>
                <div className="relative h-48 overflow-hidden bg-stone-100">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {item.badge && (
                    <span className="absolute top-3 right-3 bg-stone-900/80 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1 rounded-full shadow">
                      {item.badge}
                    </span>
                  )}
                  <span className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-md text-[#2C3E50] text-xs font-semibold px-2.5 py-0.5 rounded-lg shadow-xs">
                    {item.categoryName}
                  </span>
                </div>

                <div className="p-5">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h4 className="font-bold text-lg text-[#2C3E50]">
                      {item.name}
                    </h4>
                    <span className="font-extrabold text-lg text-sky-600 shrink-0">
                      ₹{item.price}
                    </span>
                  </div>

                  <p className="text-xs sm:text-sm text-stone-600 line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>

              <div className="px-5 pb-5 pt-2 flex items-center justify-between border-t border-stone-100">
                <span className="text-[11px] text-stone-500 font-medium">
                  100% Natural Dairy
                </span>
                <Link
                  to="/select-table"
                  className="text-xs font-bold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1 hover:underline"
                >
                  <span>Order at Table</span>
                  <span>→</span>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 📖 STORY TEASER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden shadow-xl">
          {/* Subtle glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-3xl relative z-10 space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-400">
              Our Story & Heritage
            </span>

            <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              Crafted with Udaipur's Soul. <br />
              Pure Milk, Fresh Fruits, Zero Compromise.
            </h3>

            <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
              HimCream Naturals was born in Udaipur out of a simple belief: ice cream should taste like the real ingredients it is made from. We do not use chemical stabilizers, synthetic food colors, or artificial flavor drops. Every scoop starts with rich fresh milk and handpicked fruits sourced at peak ripeness.
            </p>

            <div className="pt-2 flex flex-wrap gap-4 items-center">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm px-6 py-3 rounded-full transition shadow-lg shadow-sky-500/20"
              >
                <span>Read Full Story</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 text-stone-300 hover:text-white text-sm font-semibold px-4 py-3 hover:bg-white/10 rounded-full transition"
              >
                <span>Browse Photo Gallery</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 📱 INSTAGRAM / REELS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold uppercase tracking-wider text-pink-600 mb-1">
              <Instagram className="w-4 h-4 text-pink-500" />
              <span>@{businessInfo.contact.instagram}</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50]">
              Fresh Moments from Instagram
            </h3>
          </div>

          <a
            href={businessInfo.contact.instagramUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-pink-600 hover:text-pink-700 group"
          >
            <span>Follow Us on Instagram</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {featuredSocial.map((post) => (
            <a
              key={post.id}
              href={post.url}
              target="_blank"
              rel="noreferrer"
              className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-md bg-stone-100 block aspect-square"
            >
              <img
                src={post.image}
                alt="Instagram post"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/menu1.jpeg";
                }}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-4 flex flex-col justify-end text-white">
                <span className="text-[10px] uppercase font-bold text-pink-300">
                  {post.tag}
                </span>
                <p className="text-xs line-clamp-2 text-stone-200 mt-1">
                  {post.caption}
                </p>
                <div className="flex items-center gap-2 mt-2 text-[11px] text-stone-300 font-semibold">
                  <span>❤️ {post.likes}</span>
                  <span>💬 {post.comments}</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* 📍 VISIT US / MAP CTA BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-stone-200/80 p-6 sm:p-10 shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-600">
              Visit Us in Udaipur
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50]">
              Taste The Difference Today
            </h3>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Step into our café on Mali Colony Road, Sec 4, Udaipur. Experience fresh ice creams, loaded shakes, and slow-simmered rabdi served with warmth.
            </p>
            <div className="space-y-2 pt-2 text-sm text-stone-700">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-sky-600 shrink-0" />
                <span>{businessInfo.address.full}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-sky-600 shrink-0" />
                <span>{businessInfo.timings.hours} ({businessInfo.timings.days})</span>
              </div>
            </div>
            <div className="pt-2 flex flex-wrap gap-3">
              <a
                href={businessInfo.contact.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm px-6 py-2.5 rounded-full shadow transition"
              >
                <Navigation className="w-4 h-4" />
                <span>Get Directions</span>
              </a>
              <Link
                to="/select-table"
                className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-sm px-5 py-2.5 rounded-full transition"
              >
                <UtensilsCrossed className="w-4 h-4" />
                <span>Order at Table</span>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 h-64 sm:h-72 rounded-2xl overflow-hidden border border-stone-200 shadow-inner">
            <iframe
              title="HimCream Location Udaipur"
              src={businessInfo.contact.mapsEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
